import type { CSSProperties, ReactNode, ComponentType, MouseEvent, ComponentProps } from 'react';
import type Image from 'next/image';

/**
 * Supported icon types/libraries
 */
export type IconType = 'lucide' | 'remix' | 'data-uri';

/**
 * Predefined icon sizes matching theme configuration
 */
export type IconSize =
    | 'xxsmall' // 12px
    | 'xsmall' // 14px
    | 'small' // 16px
    | 'medium' // 18px
    | 'large' // 20px
    | 'xlarge' // 24px
    | 'xxlarge' // 32px
    | 'xxxlarge' // 40px
    | 'xxxxlarge' // 48px
    | number;

/**
 * Icon size mapping in pixels
 */
export const iconSizeMap: Record<Exclude<IconSize, number>, number> = {
    xxsmall: 12,
    xsmall: 14,
    small: 16,
    medium: 18,
    large: 20,
    xlarge: 24,
    xxlarge: 32,
    xxxlarge: 40,
    xxxxlarge: 48,
};

/**
 * Props for external Icon component (lucide-react style)
 */
export interface IconComponentProps {
    size?: number;
    color?: string;
    strokeWidth?: number;
    className?: string;
}

export interface IconProps {
    /**
     * Icon name - will be looked up from the icon library
     * For lucide: use PascalCase (e.g., 'Home', 'ShoppingCart', 'ArrowLeft')
     * @example 'Home', 'User', 'Settings', 'ShoppingCart'
     */
    name?: string;

    /**
     * Icon type/library to use
     * @default 'lucide'
     */
    type?: IconType;

    /**
     * Data URI props when using type="data-uri"
     */
    dataUriProps?: ComponentProps<typeof Image>;

    /**
     * Pass icon component directly (alternative to name lookup)
     * @example
     * import { Home } from 'lucide-react';
     * <Icon icon={Home} />
     */
    icon?: ComponentType<IconComponentProps>;

    /**
     * Size of the icon - predefined or custom number (px)
     * @default 'xlarge' (24px)
     */
    size?: IconSize;

    /**
     * Icon color - CSS value or Tailwind class via className
     * @default 'currentColor'
     */
    color?: string;

    /**
     * Additional CSS classes (Tailwind)
     */
    className?: string;

    /**
     * Container CSS classes
     */
    containerClassName?: string;

    /**
     * Whether the icon is disabled
     * @default false
     */
    disabled?: boolean;

    /**
     * Control visibility
     * @default true
     */
    showIf?: boolean;

    /**
     * Stroke width for SVG
     * @default 2
     */
    strokeWidth?: number;

    /**
     * Fill color
     */
    fill?: string;

    /**
     * Stroke color
     */
    stroke?: string;

    /**
     * Inline SVG children (alternative to name/icon)
     */
    children?: ReactNode;

    /**
     * Click handler
     */
    onClick?: (event: MouseEvent<HTMLSpanElement>) => void;

    /**
     * Container inline style
     */
    containerStyle?: CSSProperties;

    /**
     * Accessibility label
     */
    'aria-label'?: string;

    /**
     * Hide from screen readers (decorative icon)
     */
    'aria-hidden'?: boolean;

    /**
     * Test ID
     */
    testID?: string;
}
